import type {
  DistiListingConfigUpdate,
  DistiListingDeleteRequest,
  DistiListingDeleteResponse,
  DistiListingParseSpecPasteResponse,
  DistiListingPreviewResponse,
  DistiListingPullResponse,
  DistiListingScrapeSpecResponse,
  DistiListingSearchParams,
  DistiListingSearchResponse,
  Vendor,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export async function searchDistiListings(
  params: DistiListingSearchParams & { signal?: AbortSignal },
): Promise<DistiListingSearchResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page ?? 1));
  qs.set('pageSize', String(params.pageSize ?? 50));
  if (params.productCode) qs.set('productCode', params.productCode);
  if (params.q) qs.set('q', params.q);
  if (params.distiId) qs.set('distiId', params.distiId);
  if (params.inStockOnly) qs.set('inStockOnly', 'true');
  if (params.computerType) qs.set('computerType', params.computerType);
  if (params.screenSize) qs.set('screenSize', params.screenSize);
  if (params.cpuBuckets) qs.set('cpuBuckets', params.cpuBuckets);
  if (params.ramGb) qs.set('ramGb', params.ramGb);
  if (params.storageGb) qs.set('storageGb', params.storageGb);

  const response = await fetch(`${API_BASE_URL}/disti-listings/search?${qs}`, {
    signal: params.signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to search disti listings: ${response.status} ${response.statusText}`,
    );
  }
  const data = await response.json();
  return {
    items: data.items ?? [],
    pagination: data.pagination ?? {
      page: 1,
      pageSize: 50,
      totalItems: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
    },
  };
}

export async function updateDistiListingConfig(
  vendorId: string,
  patch: DistiListingConfigUpdate,
  signal?: AbortSignal,
): Promise<Vendor> {
  const response = await fetch(
    `${API_BASE_URL}/vendors/${encodeURIComponent(vendorId)}/listings/config`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to update listing config: ${response.status} ${text}`,
    );
  }
  return response.json();
}

export async function previewDistiListingPull(
  vendorId: string,
  draft: DistiListingConfigUpdate,
  signal?: AbortSignal,
): Promise<DistiListingPreviewResponse> {
  const response = await fetch(
    `${API_BASE_URL}/vendors/${encodeURIComponent(vendorId)}/listings/preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft),
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Preview failed: ${response.status} ${text}`);
  }
  return response.json();
}

/**
 * Dry-run preview against a tech-uploaded file plus the current
 * draft (unsaved) config. Same response shape as
 * `previewDistiListingPull` (counts + samples, no persistence).
 *
 * The file's bytes are POSTed as the raw body; the draft config
 * rides along in a base64-encoded `X-Draft-Config` header so techs
 * can iterate on rules — edit, re-preview against the same file —
 * without saving in between. Pass `null` for `draft` to preview
 * against the saved vendor config as-is.
 */
export async function previewDistiListingUpload(
  vendorId: string,
  file: File | Blob,
  draft: DistiListingConfigUpdate | null,
  signal?: AbortSignal,
): Promise<DistiListingPreviewResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/octet-stream',
  };
  if (draft) {
    // btoa requires Latin-1; encode UTF-8 → bytes → Latin-1 → base64 so
    // any non-ASCII chars in the rules (e.g. "®" / "™" in match values)
    // round-trip cleanly.
    const json = JSON.stringify(draft);
    const utf8Bytes = new TextEncoder().encode(json);
    let binary = '';
    utf8Bytes.forEach((b) => {
      binary += String.fromCharCode(b);
    });
    headers['X-Draft-Config'] = btoa(binary);
  }
  const response = await fetch(
    `${API_BASE_URL}/vendors/${encodeURIComponent(vendorId)}/listings/preview-upload`,
    {
      method: 'POST',
      headers,
      body: file,
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Preview-upload failed: ${response.status} ${text}`);
  }
  return response.json();
}

export interface RunDistiListingPullOptions {
  force?: boolean;
  /**
   * When true, the server bypasses the hash-equality short-circuit so
   * every classified row PATCHes through the full payload — refreshing
   * extracted specs and calculated descriptions for historical rows.
   */
  forceRewrite?: boolean;
  signal?: AbortSignal;
}

/**
 * Triggers a real (persisting) pull. Resolves to the run summary on
 * success. When the empty-result guard fires (zero rows would survive
 * AND prior rows exist), the server returns 400 with
 * `{error: "ALL_FILTERED", counts}` — this function throws a typed
 * error so callers can offer a "Run anyway" confirmation.
 */
export async function runDistiListingPull(
  vendorId: string,
  options: RunDistiListingPullOptions = {},
): Promise<DistiListingPullResponse> {
  const params = new URLSearchParams();
  if (options.force) params.set('force', 'true');
  if (options.forceRewrite) params.set('force_rewrite', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(
    `${API_BASE_URL}/vendors/${encodeURIComponent(vendorId)}/listings/pull${qs}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: options.signal,
    },
  );
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    if (data && data.error === 'ALL_FILTERED') {
      const err = new AllFilteredError(data.message || 'All rows filtered out', data.counts);
      throw err;
    }
    const text = data ? JSON.stringify(data) : await response.text().catch(() => '');
    throw new Error(`Pull failed: ${response.status} ${text}`);
  }
  return response.json();
}

/**
 * Upload a feed file (CSV / TSV / TXT / ZIP) and run the pipeline
 * against its bytes. Same pull semantics as `runDistiListingPull` —
 * empty-result guard, hash-diff, stale-aging all apply identically.
 *
 * Used for vendors whose feed sits behind a login the daily cron
 * can't reach (Synnex extranet, Dicker portal, …).
 *
 * The file's bytes are POSTed as the raw body. Format / delimiter
 * is auto-detected: ZIP magic-bytes unwrap to inner-file extension
 * (`.csv` → comma, `.tsv` / `.txt` → tab); otherwise the vendor's
 * configured `format` (csv / tsv / json) controls.
 */
export async function uploadDistiListingFile(
  vendorId: string,
  file: File | Blob,
  options: RunDistiListingPullOptions = {},
): Promise<DistiListingPullResponse> {
  const params = new URLSearchParams();
  if (options.force) params.set('force', 'true');
  if (options.forceRewrite) params.set('force_rewrite', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(
    `${API_BASE_URL}/vendors/${encodeURIComponent(vendorId)}/listings/upload${qs}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: file,
      signal: options.signal,
    },
  );
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    if (data && data.error === 'ALL_FILTERED') {
      throw new AllFilteredError(
        data.message || 'All rows filtered out',
        data.counts,
      );
    }
    const text = data ? JSON.stringify(data) : await response.text().catch(() => '');
    throw new Error(`Upload failed: ${response.status} ${text}`);
  }
  return response.json();
}

/**
 * Bulk-delete listings for a disti by SKU and/or seen-at range.
 *
 * Pass ``dryRun: true`` to get only ``matchedCount`` back without
 * deleting — used by the UI to show "this will delete N listings"
 * before the user confirms.
 *
 * For ghost-row cleanup, set ``distiSku: ''``. For "purge stale rows",
 * set ``seenBefore`` to (e.g.) 30 days ago.
 */
export async function deleteDistiListings(
  vendorId: string,
  body: DistiListingDeleteRequest,
  signal?: AbortSignal,
): Promise<DistiListingDeleteResponse> {
  const response = await fetch(
    `${API_BASE_URL}/vendors/${encodeURIComponent(vendorId)}/listings/delete`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Delete failed: ${response.status} ${text}`);
  }
  return response.json();
}

/**
 * POST /api/disti-listings/{listingId}/scrape-spec
 *
 * Trigger a one-shot URL scrape for a single listing. Returns the
 * post-scrape state (success or recoverable failure) in a single
 * response shape — the picker swaps the row in place from `result.listing`
 * regardless of `status`. Hard errors (404 / 422) throw.
 */
export async function scrapeListingSpec(
  listingId: string,
  signal?: AbortSignal,
): Promise<DistiListingScrapeSpecResponse> {
  const response = await fetch(
    `${API_BASE_URL}/disti-listings/${encodeURIComponent(listingId)}/scrape-spec`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Scrape failed: ${response.status} ${text}`);
  }
  return response.json();
}

/**
 * POST /api/disti-listings/{listingId}/parse-spec-paste
 *
 * Run the regex spec extractor over a tech-pasted spec block and
 * persist the result to the listing's spec columns. Returns the
 * post-write state in the same shape as `scrapeListingSpec` so the
 * picker can swap rows in place from one handler.
 */
export async function parseSpecPaste(
  listingId: string,
  text: string,
  signal?: AbortSignal,
): Promise<DistiListingParseSpecPasteResponse> {
  const response = await fetch(
    `${API_BASE_URL}/disti-listings/${encodeURIComponent(listingId)}/parse-spec-paste`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
      signal,
    },
  );
  if (!response.ok) {
    const t = await response.text().catch(() => '');
    throw new Error(`Parse failed: ${response.status} ${t}`);
  }
  return response.json();
}

/**
 * Thrown by `runDistiListingPull` when the empty-result guard refuses a
 * run. Carries the run counts so the UI can show what would have happened
 * without a separate Preview round-trip.
 */
export class AllFilteredError extends Error {
  readonly code = 'ALL_FILTERED' as const;
  readonly counts: Record<string, number>;

  constructor(message: string, counts: Record<string, number>) {
    super(message);
    this.name = 'AllFilteredError';
    this.counts = counts;
  }
}
